import Papa from "papaparse";
import {
  HEADER_MAPPING,
  cleanCsvValue,
  type CsvCleanedValue,
} from "@/lib/import-config";

export type DomainImportRow = Record<string, CsvCleanedValue> & {
  domain: string;
};

export async function parseAndFormatCsv(
  file: File,
): Promise<DomainImportRow[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: "greedy",
      complete: (results) => {
        try {
          const rawData = results.data as string[][];

          let headerRowIndex = -1;

          for (let i = 0; i < rawData.length; i++) {
            const row = rawData[i];
            const hasDomainColumn = row.some(
              (cell) =>
                cell &&
                cell
                  .toString()
                  .toLowerCase()
                  .replace(/\s+/g, "")
                  .includes("domainname")
            );

            if (hasDomainColumn) {
              headerRowIndex = i;
              break;
            }
          }

          if (headerRowIndex === -1) {
            throw new Error("No header row found (looking for 'domain name')");
          }

          const headers = rawData[headerRowIndex];
          const dataRows = rawData.slice(headerRowIndex + 1);

          const formattedData = dataRows
            .map((row): DomainImportRow | null => {
              const dbRow: Record<string, CsvCleanedValue> = {};
              let hasData = false;

              row.forEach((cellValue, index) => {
                const headerName = headers[index];
                if (!headerName) return;

                const cleanKey = headerName
                  .toString()
                  .replace(/[\r\n\t\u2028\u2029]+/g, " ")
                  .replace(/\s+/g, " ")
                  .trim()
                  .toLowerCase();

                const dbKey = HEADER_MAPPING[cleanKey];

                if (dbKey) {
                  dbRow[dbKey] = cleanCsvValue(dbKey, cellValue);
                  if (cellValue) hasData = true;
                }
              });

              const domain = dbRow.domain;
              const isValidDomain =
                typeof domain === "string" && domain.includes(".");

              // Rows without a usable domain are dropped silently: the user is
              // never told how many lines of their file were skipped.
              if (!hasData || !isValidDomain) return null;

              return { ...dbRow, domain };
            })
            .filter((r): r is DomainImportRow => r !== null);

          resolve(formattedData);
        } catch (err) {
          reject(err);
        }
      },
      error: (err) => reject(err),
    });
  });
}
